import { useState, useEffect, useCallback } from 'react'
import type { Documento } from '@services/db.js'
import {
  getDocumentos,
  addDocumento,
  updateDocumento,
  deleteDocumento,
  getDocumentosStats,
} from '@services/crud.js'

export type { Documento }

export interface DocumentosData {
  documentos: Documento[]
  stats: Record<string, number>
  isLoading: boolean
}

const INITIAL: DocumentosData = { documentos: [], stats: {}, isLoading: true }

export function useDocumentos() {
  const [data, setData] = useState<DocumentosData>(INITIAL)

  const load = useCallback(async () => {
    const [docs, stats] = await Promise.all([getDocumentos(), getDocumentosStats()])
    setData({ documentos: docs, stats, isLoading: false })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const add = useCallback(
    async (payload: Omit<Documento, 'id' | 'criadoEm'>, arquivo?: Blob | File | null) => {
      await addDocumento(payload, arquivo)
      await load()
    },
    [load],
  )

  const update = useCallback(
    async (
      id: number,
      patch: Partial<Omit<Documento, 'id' | 'criadoEm'>>,
      arquivo?: Blob | File | null,
    ) => {
      await updateDocumento(id, patch, arquivo)
      await load()
    },
    [load],
  )

  const remove = useCallback(
    async (id: number) => {
      await deleteDocumento(id)
      await load()
    },
    [load],
  )

  return { ...data, reload: load, add, update, remove }
}
